import React, { useEffect, useState, useRef } from "react";
import {
  Search, Plus, ScanLine, X, Loader2, Trash2, Save, Mail, Phone, Globe, MapPin,
  Building2, Tag, UserPlus, Store, StickyNote, Camera,
} from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const inputCls = "w-full px-3 py-2.5 rounded-lg dark-input text-sm";
const labelCls = "block text-[10px] font-bold text-zinc-500 uppercase tracking-wider mb-1.5";

const emptyForm = { name: "", company: "", title: "", phones: [""], emails: [""], website: "", address: "", tags: "", context: "", card_image: "" };

const fileToDataUrl = (file) => new Promise((res, rej) => {
  const r = new FileReader(); r.onload = () => res(r.result); r.onerror = rej; r.readAsDataURL(file);
});

const fmtTime = (iso) => { try { return new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" }); } catch { return ""; } };

export default function ContactsManager() {
  const [contacts, setContacts] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null); // contact object when editing, null when creating
  const [form, setForm] = useState(emptyForm);
  const [sendIntro, setSendIntro] = useState(false);
  const [saving, setSaving] = useState(false);
  const [notes, setNotes] = useState([]);
  const [noteDraft, setNoteDraft] = useState("");
  const [scanPreview, setScanPreview] = useState("");
  const scanRef = useRef(null);

  const load = (query = "") => { setLoading(true); api.listContacts(query).then(setContacts).catch(() => {}).finally(() => setLoading(false)); };
  useEffect(() => { load(); }, []);
  useEffect(() => { const t = setTimeout(() => load(q), 300); return () => clearTimeout(t); }, [q]);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const openNew = () => { setEditing(null); setForm(emptyForm); setSendIntro(false); setNotes([]); setNoteDraft(""); setScanPreview(""); setModalOpen(true); };
  const openEdit = (c) => {
    setEditing(c);
    setScanPreview("");
    setForm({
      name: c.name || "", company: c.company || "", title: c.title || "",
      phones: (c.phones && c.phones.length ? c.phones : [""]), emails: (c.emails && c.emails.length ? c.emails : [""]),
      website: c.website || "", address: c.address || "", tags: (c.tags || []).join(", "), context: c.context || "", card_image: c.card_image || "",
    });
    setNotes(c.notes || []); setNoteDraft(""); setSendIntro(false); setModalOpen(true);
  };

  const onScanFile = async (file) => {
    if (!file) return;
    setScanning(true);
    try {
      const dataUrl = await fileToDataUrl(file);
      const res = await api.scanCard(dataUrl, file.type || "image/jpeg");
      const p = res.parsed || {};
      setEditing(null);
      setScanPreview(dataUrl);
      setForm({
        ...emptyForm,
        name: p.name || "", company: p.company || "", title: p.title || "",
        phones: (p.phones && p.phones.length ? p.phones : [""]), emails: (p.emails && p.emails.length ? p.emails : [""]),
        website: p.website || "", address: p.address || "", tags: (p.tags || []).join(", "), card_image: res.card_image || "",
      });
      setSendIntro(false); setNotes([]); setNoteDraft(""); setModalOpen(true);
      toast.success("Card scanned — review and save");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not read the card. Try a clearer photo.");
    } finally { setScanning(false); if (scanRef.current) scanRef.current.value = ""; }
  };

  const save = async () => {
    if (!form.name.trim() && !form.company.trim()) { toast.error("Add at least a name or a company"); return; }
    setSaving(true);
    const payload = {
      name: form.name, company: form.company, title: form.title,
      phones: form.phones.filter((p) => p && p.trim()), emails: form.emails.filter((e) => e && e.trim()),
      website: form.website, address: form.address,
      tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean), context: form.context, card_image: form.card_image,
    };
    try {
      if (editing) {
        await api.updateContact(editing.id, payload);
        toast.success("Contact updated");
      } else {
        payload.send_intro = sendIntro;
        const created = await api.createContact(payload);
        if (sendIntro) toast.success(created.intro_sent ? "Saved — intro email sent" : "Saved (intro email not sent — check email)");
        else toast.success("Contact saved");
      }
      setModalOpen(false);
      load(q);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save contact");
    } finally { setSaving(false); }
  };

  const removeContact = async (c) => {
    setContacts((s) => s.filter((x) => x.id !== c.id));
    try { await api.deleteContact(c.id); toast.success("Contact removed"); } catch { load(q); }
  };

  const addNote = async () => {
    const text = noteDraft.trim();
    if (!text || !editing) return;
    try {
      const note = await api.addContactNote(editing.id, text);
      setNotes((n) => [note, ...n]);
      setNoteDraft("");
    } catch (e) { toast.error("Could not add note"); }
  };
  const deleteNote = async (nid) => {
    setNotes((n) => n.filter((x) => x.id !== nid));
    try { await api.deleteContactNote(editing.id, nid); } catch { /* ignore */ }
  };

  const convertVendor = async () => {
    if (!editing) return;
    try { const r = await api.convertContactToVendor(editing.id); toast.success(r.message); setEditing((c) => ({ ...c, converted_vendor_id: r.vendor_id })); setContacts((s) => s.map((x) => x.id === editing.id ? { ...x, converted_vendor_id: r.vendor_id } : x)); } catch (e) { toast.error(e?.response?.data?.detail || "Could not convert"); }
  };
  const convertBusiness = async () => {
    if (!editing) return;
    try { const r = await api.convertContactToBusiness(editing.id); toast.success(r.message); setEditing((c) => ({ ...c, converted_business_id: r.business_id })); setContacts((s) => s.map((x) => x.id === editing.id ? { ...x, converted_business_id: r.business_id } : x)); } catch (e) { toast.error(e?.response?.data?.detail || "Could not convert"); }
  };

  const setListItem = (key, i, v) => setForm((f) => ({ ...f, [key]: f[key].map((x, idx) => idx === i ? v : x) }));
  const addListItem = (key) => setForm((f) => ({ ...f, [key]: [...f[key], ""] }));
  const removeListItem = (key, i) => setForm((f) => ({ ...f, [key]: f[key].filter((_, idx) => idx !== i).length ? f[key].filter((_, idx) => idx !== i) : [""] }));

  return (
    <div data-testid="contacts-manager">
      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-6">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search contacts by name, company, tag, email…" className={`${inputCls} pl-9`} data-testid="contacts-search" />
        </div>
        <div className="flex gap-2">
          <button onClick={() => scanRef.current?.click()} disabled={scanning} className="px-4 py-2.5 bg-primary-600 text-white rounded-xl text-sm font-bold hover:bg-primary-500 transition flex items-center disabled:opacity-60 whitespace-nowrap" data-testid="scan-card-btn">
            {scanning ? <Loader2 size={16} className="mr-2 animate-spin" /> : <ScanLine size={16} className="mr-2" />} Scan Business Card
          </button>
          <input ref={scanRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => onScanFile(e.target.files?.[0])} />
          <button onClick={openNew} className="px-4 py-2.5 bg-white/10 text-white rounded-xl text-sm font-bold hover:bg-white/20 transition flex items-center whitespace-nowrap" data-testid="add-contact-btn"><Plus size={16} className="mr-2" /> Add</button>
        </div>
      </div>

      {/* List */}
      {loading ? (
        <div className="glass-panel rounded-2xl p-12 text-center text-zinc-500">Loading contacts…</div>
      ) : contacts.length === 0 ? (
        <div className="glass-panel rounded-2xl p-12 text-center">
          <ScanLine size={32} className="text-zinc-600 mx-auto mb-3" />
          <div className="text-white font-bold">No contacts yet</div>
          <div className="text-sm text-zinc-500 mt-1">Scan a business card or add someone manually to start your rolodex.</div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
          {contacts.map((c) => (
            <button key={c.id} onClick={() => openEdit(c)} className="text-left glass-panel rounded-2xl p-4 hover:bg-white/5 transition border border-white/10" data-testid="contact-row">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="text-sm font-bold text-white truncate">{c.name || c.company || "Unnamed"}</div>
                  <div className="text-xs text-zinc-500 truncate">{[c.title, c.company].filter(Boolean).join(" · ")}</div>
                </div>
                <div className="flex gap-1 flex-shrink-0">
                  {c.converted_vendor_id && <span title="In Vendor Directory" className="p-1 rounded-md bg-teal-500/15 text-teal-400"><Store size={12} /></span>}
                  {c.converted_business_id && <span title="Is a Business" className="p-1 rounded-md bg-blue-500/15 text-blue-400"><Building2 size={12} /></span>}
                </div>
              </div>
              <div className="mt-2 space-y-1">
                {(c.emails || [])[0] && <div className="text-[11px] text-zinc-400 flex items-center gap-1.5 truncate"><Mail size={11} className="text-zinc-500" /> {(c.emails || [])[0]}</div>}
                {(c.phones || [])[0] && <div className="text-[11px] text-zinc-400 flex items-center gap-1.5 truncate"><Phone size={11} className="text-zinc-500" /> {(c.phones || [])[0]}</div>}
              </div>
              {(c.tags || []).length > 0 && (
                <div className="mt-2.5 flex flex-wrap gap-1">
                  {(c.tags || []).slice(0, 4).map((t) => <span key={t} className="px-2 py-0.5 rounded-full bg-white/5 text-[10px] font-bold text-zinc-400">{t}</span>)}
                </div>
              )}
            </button>
          ))}
        </div>
      )}

      {/* Modal */}
      {modalOpen && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/80 backdrop-blur-md p-4" data-testid="contact-modal">
          <div className="glass-panel bg-[#0a0a0b]/95 rounded-3xl shadow-2xl w-full max-w-2xl relative fade-in overflow-hidden border border-white/10 flex flex-col" style={{ maxHeight: "92vh" }}>
            <div className="px-6 py-4 border-b border-white/10 flex justify-between items-center bg-white/5">
              <div className="flex items-center gap-2"><UserPlus size={17} className="text-primary-400" /><h3 className="text-base font-bold text-white">{editing ? "Edit Contact" : "New Contact"}</h3></div>
              <button onClick={() => setModalOpen(false)} className="text-zinc-500 hover:text-white" data-testid="contact-modal-close"><X /></button>
            </div>

            <div className="flex-1 overflow-y-auto p-6 space-y-4 hide-scrollbar">
              {(form.card_image || scanPreview) && (
                <div className="rounded-xl overflow-hidden border border-white/10">
                  <img src={editing ? api.contactCardUrl(editing.id) : scanPreview} alt="business card" className="w-full max-h-40 object-contain bg-black/40" />
                  {!editing && <div className="px-3 py-2 text-[11px] text-green-400 flex items-center gap-1.5 bg-green-500/5"><Camera size={12} /> Card image attached — it will be saved with this contact.</div>}
                </div>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div><label className={labelCls}>Full Name</label><input value={form.name} onChange={(e) => setField("name", e.target.value)} className={inputCls} data-testid="contact-name-input" /></div>
                <div><label className={labelCls}>Company</label><input value={form.company} onChange={(e) => setField("company", e.target.value)} className={inputCls} data-testid="contact-company-input" /></div>
              </div>
              <div><label className={labelCls}>Title / Role</label><input value={form.title} onChange={(e) => setField("title", e.target.value)} className={inputCls} /></div>

              <div>
                <label className={labelCls}>Phone(s)</label>
                {form.phones.map((p, i) => (
                  <div key={i} className="flex gap-2 mb-2">
                    <input value={p} onChange={(e) => setListItem("phones", i, e.target.value)} className={inputCls} data-testid="contact-phone-input" />
                    <button onClick={() => removeListItem("phones", i)} className="px-2 text-zinc-500 hover:text-red-400"><Trash2 size={14} /></button>
                  </div>
                ))}
                <button onClick={() => addListItem("phones")} className="text-xs font-bold text-primary-400 hover:text-primary-300 flex items-center"><Plus size={12} className="mr-1" /> Add phone</button>
              </div>

              <div>
                <label className={labelCls}>Email(s)</label>
                {form.emails.map((e2, i) => (
                  <div key={i} className="flex gap-2 mb-2">
                    <input value={e2} onChange={(e) => setListItem("emails", i, e.target.value)} className={inputCls} data-testid="contact-email-input" />
                    <button onClick={() => removeListItem("emails", i)} className="px-2 text-zinc-500 hover:text-red-400"><Trash2 size={14} /></button>
                  </div>
                ))}
                <button onClick={() => addListItem("emails")} className="text-xs font-bold text-primary-400 hover:text-primary-300 flex items-center"><Plus size={12} className="mr-1" /> Add email</button>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div><label className={labelCls}>Website</label><input value={form.website} onChange={(e) => setField("website", e.target.value)} className={inputCls} /></div>
                <div><label className={labelCls}>Address</label><input value={form.address} onChange={(e) => setField("address", e.target.value)} className={inputCls} /></div>
              </div>
              <div><label className={labelCls}>Tags (comma separated)</label><input value={form.tags} onChange={(e) => setField("tags", e.target.value)} placeholder="mixologist, bar, catering" className={inputCls} data-testid="contact-tags-input" /></div>
              <div><label className={labelCls}>Where we met / Context</label><input value={form.context} onChange={(e) => setField("context", e.target.value)} placeholder="e.g. Met at Denver expo, June 2026" className={inputCls} /></div>

              {!editing && (
                <label className="flex items-center gap-2.5 p-3 rounded-xl bg-white/5 border border-white/10 cursor-pointer" data-testid="contact-send-intro">
                  <input type="checkbox" checked={sendIntro} onChange={(e) => setSendIntro(e.target.checked)} className="w-4 h-4 accent-primary-500" />
                  <span className="text-sm text-zinc-200 flex items-center gap-1.5"><Mail size={14} className="text-primary-400" /> Email them my DONDRÁE info & intro</span>
                </label>
              )}

              {/* Notes (existing contacts only) */}
              {editing && (
                <div className="pt-2 border-t border-white/10">
                  <div className="flex items-center gap-2 text-xs font-bold text-zinc-400 uppercase tracking-wider mb-2"><StickyNote size={13} /> Notes</div>
                  <div className="flex gap-2 mb-2">
                    <input value={noteDraft} onChange={(e) => setNoteDraft(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addNote(); } }} placeholder="Add a note — time-stamped automatically…" className={inputCls} data-testid="contact-note-input" />
                    <button onClick={addNote} className="px-3 rounded-lg bg-primary-600 text-white text-xs font-bold hover:bg-primary-500 transition flex items-center whitespace-nowrap" data-testid="contact-note-add"><Plus size={12} className="mr-1" /> Add</button>
                  </div>
                  {notes.length === 0 && <p className="text-zinc-600 text-xs">No notes yet.</p>}
                  <div className="space-y-2">
                    {notes.map((n) => (
                      <div key={n.id} className="flex items-start gap-3 p-2.5 rounded-lg bg-black/40 border border-white/5" data-testid="contact-note-row">
                        <div className="flex-1 min-w-0"><div className="text-sm text-zinc-200 whitespace-pre-wrap break-words">{n.text}</div><div className="text-[10px] text-zinc-500 mt-1">{fmtTime(n.at)}</div></div>
                        <button onClick={() => deleteNote(n.id)} className="text-zinc-500 hover:text-red-400 flex-shrink-0"><Trash2 size={13} /></button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Convert (existing contacts only) */}
              {editing && (
                <div className="pt-2 border-t border-white/10">
                  <div className="text-xs font-bold text-zinc-400 uppercase tracking-wider mb-2">Convert</div>
                  <div className="flex flex-wrap gap-2">
                    <button onClick={convertVendor} className="px-3 py-2 rounded-lg bg-teal-500/15 text-teal-300 text-xs font-bold hover:bg-teal-500/25 transition flex items-center" data-testid="convert-vendor-btn"><Store size={14} className="mr-1.5" /> {editing.converted_vendor_id ? "Update in Vendor Directory" : "Add to Vendor Directory"}</button>
                    <button onClick={convertBusiness} className="px-3 py-2 rounded-lg bg-blue-500/15 text-blue-300 text-xs font-bold hover:bg-blue-500/25 transition flex items-center" data-testid="convert-business-btn"><Building2 size={14} className="mr-1.5" /> {editing.converted_business_id ? "Linked as Business" : "Add as Business"}</button>
                  </div>
                </div>
              )}
            </div>

            <div className="px-6 py-4 border-t border-white/10 flex justify-between items-center bg-white/5">
              {editing ? (
                <button onClick={() => { removeContact(editing); setModalOpen(false); }} className="px-4 py-2.5 rounded-xl bg-white/5 text-red-400 text-sm font-bold hover:bg-red-500/15 transition flex items-center" data-testid="contact-delete-btn"><Trash2 size={14} className="mr-1.5" /> Delete</button>
              ) : <span />}
              <div className="flex gap-2">
                <button onClick={() => setModalOpen(false)} className="px-4 py-2.5 rounded-xl bg-white/5 text-zinc-300 text-sm font-bold hover:bg-white/10 transition">Cancel</button>
                <button onClick={save} disabled={saving} className="px-5 py-2.5 rounded-xl bg-primary-600 text-white text-sm font-bold hover:bg-primary-500 transition disabled:opacity-60 flex items-center" data-testid="contact-save-btn">{saving ? <Loader2 size={15} className="mr-2 animate-spin" /> : <Save size={15} className="mr-2" />} Save</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
